import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/browser'

import { getZendeskToken } from 'services/apiMotorBgc'

const waitForZendesk = (callback) => {
  if (window.zE) {
    callback()
    return
  }

  let attempts = 0

  const intervalId = setInterval(() => {
    attempts += 1

    if (window.zE) {
      clearInterval(intervalId)
      callback()
      return
    }

    // Timeout after 6 seconds
    if (attempts >= 20) {
      clearInterval(intervalId)

      Sentry.captureMessage('Zendesk widget failed to load')
    }
  }, 300)

  return () => clearInterval(intervalId)
}

// Use this hook once on App.jsx to customize the Zendesk widget used on all pages.
const useZendeskWidgetCustomized = () => {
  const initializedUser = useRef(null)

  const apiKey = useSelector((state) => state.auth.api_key)
  const userId = useSelector((state) => state.auth.sub)
  const name = useSelector((state) => state.auth.name)

  useEffect(() => {
    // User logout
    if (!userId) {
      if (initializedUser.current) {
        if (window.zE) window.zE('messenger', 'logoutUser')
        initializedUser.current = null
      }

      return
    }

    // Wait auth data
    if (!apiKey || !name) {
      return
    }

    // User already initialized
    if (initializedUser.current === userId) {
      return
    }

    const authenticateZendesk = () => {
      window.zE('messenger', 'loginUser', (callback) => {
        getZendeskToken(apiKey, userId, name)
          .then((res) => {
            callback(res.data.token)

            initializedUser.current = userId

            window.zE('messenger', 'show')
          })
          .catch((error) => {
            console.error('Zendesk authentication failed:', error)

            initializedUser.current = null
          })
      })
    }

    const cleanup = waitForZendesk(authenticateZendesk)

    return cleanup
  }, [apiKey, userId, name])
}

export default useZendeskWidgetCustomized
